import { differenceInDays, isPast } from 'date-fns'

/**
 * Credit-card expiry predicates, kept behaviorally identical to the app-shell
 * copies in `domains/app-shell/src/common/utils/user.ts` so the billing-page
 * notices show exactly when the app-shell billing banner does. The frontend
 * package boundary forbids importing them, so both suites run the same parity
 * fixture table — see creditCardExpiry.test.ts.
 */

export type CreditCardExpiry = {
  expMonth: string
  expYear: string
}

export function formatCreditCardExpiryToDate(
  cardDetails: CreditCardExpiry | null | undefined,
): Date {
  return new Date(`
  ${Number(cardDetails?.expYear) || 2023},
  ${Number(cardDetails?.expMonth) || 1},
  30, 23:59:00`)
}

export function calculateDaysTillCreditCardExpires(
  cardDetails: CreditCardExpiry | null | undefined,
): number {
  if (!cardDetails?.expYear || !cardDetails?.expMonth) return 0

  // We set the expiry to the 30th of the provided card month at midnight
  // Note - we use the specified time in hours to include that day in the counted days
  const cardExpiryDate = formatCreditCardExpiryToDate(cardDetails)

  const isCardAlreadyExpired = isCreditCardExpired(cardDetails)
  const todaysDate = new Date()

  const remainingDaysTillExpiry = isCardAlreadyExpired
    ? 0
    : differenceInDays(cardExpiryDate, todaysDate)

  return remainingDaysTillExpiry
}

export function isCreditCardExpired(
  cardDetails: CreditCardExpiry | null | undefined,
): boolean {
  if (!cardDetails?.expYear || !cardDetails?.expMonth) return true

  const cardExpiryDate = formatCreditCardExpiryToDate(cardDetails)

  const isCardAlreadyExpired = isPast(cardExpiryDate)

  return isCardAlreadyExpired
}

export function isCreditCardExpiring(
  cardDetails: CreditCardExpiry | null | undefined,
): boolean {
  const remainingDaysTillExpiry =
    calculateDaysTillCreditCardExpires(cardDetails)
  const isCardExpiringInNext30Days = remainingDaysTillExpiry <= 30

  return isCardExpiringInNext30Days
}

export function hasExpiredOrExpiringCreditCard(
  cardDetails: CreditCardExpiry | null | undefined,
): boolean {
  return isCreditCardExpired(cardDetails) || isCreditCardExpiring(cardDetails)
}
